import { inject, Injectable, InputSignalWithTransform } from '@angular/core';
import { catchError, firstValueFrom, map, Observable, of, shareReplay, startWith, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { Page } from '../../../@common/entities/page.interface';
import { Logger } from '../../../@common/log/logger';
import { ManagePlaylistBody, Playlist, PlaylistInfo, SortOrder, V2PlaylistsResource } from '../../../@generated';
import { NotificationService } from '../../notification/application/notification.service';
import { PlaylistInfoSort } from 'src/app/@generated/model/playlist-info-sort';

@Injectable({
    providedIn: 'root',
})
export class PlaylistService {
    private playlistResource = inject(V2PlaylistsResource);
    private notificationService = inject(NotificationService);
    private logger = inject(Logger);

    private refreshPlaylists$ = new Subject<void>();
    private allPlaylists$ = this.setupPlaylists();

    private setupPlaylists(): Observable<Page<PlaylistInfo>> {
        return this.refreshPlaylists$.pipe(
            startWith(null),
            switchMap(() => this.getPlaylists(1, 9999)),
            shareReplay(1)
        );
    }

    public getUsersPlaylists(): Observable<PlaylistInfo[]> {
        return this.allPlaylists$.pipe(
            map(page => page.results)
        );
    }
    
    public getPlaylists(page: number, pageSize: number, query?: string, sort?: string, order?: 'asc' | 'desc'): Observable<Page<PlaylistInfo>> {
        return this.playlistResource.getPlaylists(page, pageSize).pipe(
            shareReplay(1),
            catchError(e => {
                this.logger.apiError('Get playlists failed', e);
                return of({ count: 0, results: [] });
            })
        );
    }
    

    public getPlaylist(id: number): Observable<Playlist | null> {
        return this.playlistResource.getPlaylistById(id).pipe(
            catchError(e => {
                if (e.status === 404) {
                    this.logger.apiError('Playlist not found', e);
                    return of(null); // Return null if not found
                } else {
                    this.logger.apiError('Get playlist failed', e);
                    return throwError(() => e);
                }
            })
        );
    }

    public createPlaylist(body: ManagePlaylistBody): Observable<Playlist> {
        return this.playlistResource.createPlaylist(body).pipe(
            take(1),
            tap(() => this.refreshPlaylists$.next()),
            catchError(e => {
                this.logger.apiError('Failed to add playlist', e)
                return throwError(() => e);
            }),
        );
    }

    public updatePlaylist(id: number, body: ManagePlaylistBody) {
        return this.playlistResource.updatePlaylist(id, body)
            .pipe(
                take(1),
                tap(() => this.refreshPlaylists$.next()),
                catchError(e => {
                    this.logger.apiError('Failed to edit playlist', e)
                    return throwError(() => e);
                }),
            );
    }

    public deletePlaylist(id: number): Observable<Playlist> {
        return this.playlistResource.deletePlaylist(id).pipe(
            take(1),
            tap(() => this.refreshPlaylists$.next()),
            catchError(e => {
                this.logger.apiError('Failed to delete playlist', e)
                return throwError(() => e);
            }),
        );
    }

    public async addVideoToPlaylist(playlistId: number, videoId: number) {
        try {
            await firstValueFrom(this.playlistResource.addVideoToPlaylist(playlistId, videoId));
            this.refreshPlaylists$.next();
            this.notificationService.success({ detail: 'Video added to playlist' });
        } catch (e) {
            this.notificationService.error({ detail: 'Failed to add video to playlist' });
            this.logger.apiError('Failed to add video to playlist', e);
            throw e;
        }
    }

    public async removeVideoFromPlaylist(playlistId: number, videoId: number) {
        try {
            await firstValueFrom(this.playlistResource.removeVideoFromPlaylist(playlistId, videoId));
            this.refreshPlaylists$.next();
        } catch (e) {
            this.logger.apiError('Failed to remove video from playlist', e);
            throw e;
        }
    }

    public getPlaylistInfos(
            page: number,
            pageSize: number,
            order: SortOrder | undefined,
            sort: PlaylistInfoSort | undefined,
            query: string | undefined
        ): Observable<Page<PlaylistInfo>> {
            return this.playlistResource.getPlaylistInfo(page, pageSize, order, sort, query).pipe(
                catchError(e => {
                    this.logger.apiError('Get all author infos failed', e);
                    return of({ count: 0, results: [] });
                })
            );
        }
}